/**
 * ⚙️ IDENTIDADE DO CLIENTE — passo nº 1 da personalização.
 *
 * Aqui moram os VALORES que descrevem a clínica: nome, endereço, contato, o
 * profissional responsável, os profissionais que atendem, convênios da vitrine,
 * e-mails do dono, Pix e dados fiscais. O MECANISMO (os tipos, o vocabulário
 * CRM×CRO, `matchDoctor`, `fullAddress`…) mora em `src/shared/clinic-config.ts`,
 * que importa daqui e **re-exporta tudo**.
 *
 * O app inteiro continua importando de `@/shared/clinic-config` — não importe
 * deste arquivo direto. A separação existe pelo mesmo motivo das features
 * (BLUEPRINT §2): atualizar o molde sobrepõe `src/lib` e **preserva
 * `src/cliente`**. Se a identidade vivesse no lib, um sync apagaria o nome, o
 * CRM e as cores do cliente.
 *
 * Preenchendo isto, o nome certo aparece no header, no título da aba, no login,
 * nos documentos impressos (receita/atestado/exame) e nas mensagens de WhatsApp.
 * Checklist completo em NOVO-CLIENTE.md.
 */

import type { ClinicDoctor, Convenio, Shareholder } from "@/shared/clinic-config";
import type { EspecialidadeId } from "@/shared/especialidades";

/**
 * Identificador curto e estável da clínica (slug). Vai como `clinica_id` em
 * todo log, erro do Sentry e evento de uso (etimos-stack-tech, "Etiquetas em
 * tudo"). Não muda depois de ir para produção.
 */
export const CLINICA_ID = "clinica-brasil";

export const CLINIC_INFO = {
  name: "Clínica Brasil",
  addressLine: "Rua Tiradentes, 1091",
  neighborhood: "Centro",
  city: "Venâncio Aires",
  state: "RS",
  phone: "(51) 3741-7783",
  /** Só dígitos, com DDI 55 — usado em links wa.me (ex: "5551999990000"). */
  whatsapp: "5551986086022",
} as const;

/**
 * ⚙️ CONVÊNIOS aceitos — alimentam a seção "Convênios" da vitrine da landing
 * (só aparece com `landingVitrine` ligada).
 *
 * Clínica que atende planos: liste-os aqui (name + uma linha curta em `desc`).
 * Clínica SÓ PARTICULAR: deixe a lista **VAZIA** (`[]`) — a seção deixa de
 * mostrar a grade de planos e vira um bloco simples de "Atendimento particular".
 *
 * Isto é só o texto da vitrine. As coberturas que o app REGISTRA nos
 * atendimentos são outra coisa: `src/cliente/coverage.ts`.
 */
export const CONVENIOS: readonly Convenio[] = [];

/**
 * ⚙️ TIPO DA CLÍNICA — vocabulário, e SÓ vocabulário.
 *
 * Faz a mesma base de código falar a língua do cliente: um consultório
 * odontológico lê "CRO" onde o médico lê "CRM". Trocar isto muda PALAVRA na
 * tela, nunca comportamento.
 *
 * ⚠️ REGRA: nenhum `if (CLINIC_TYPE === "odonto")` decidindo lógica. Recurso
 * que só a odontologia usa (dente no orçamento, odontograma) ganha interruptor
 * próprio em `features.ts`. Clínica odontológica: ponha "odonto" AQUI e escolha
 * o `PACOTE_ODONTO` em `pacote.ts` — os dois andam juntos.
 */
export const CLINIC_TYPE: "medico" | "odonto" = "odonto";

/**
 * ⚙️ ESPECIALIDADE da clínica — decide quais recursos de especialidade podem
 * existir (odontograma, cirurgias, SUS, Unimed). Ver `src/shared/especialidades.ts`.
 * `CLINIC_TYPE` e `PACOTE_ID` têm de combinar com ela (um teste confere). O
 * molde nasce "odonto"; `pnpm novo-cliente` grava a escolhida.
 */
export const ESPECIALIDADE: EspecialidadeId = "odonto";

/**
 * Dados profissionais do(a) responsável técnico(a).
 * Aparecem no topo de toda receita/atestado/solicitação de exame.
 * Dentista: o número do CRO vai no campo `crm`; o rótulo sai do `CLINIC_VOCAB`
 * (basta o `CLINIC_TYPE` acima estar em "odonto").
 */
export const DOCTOR_INFO = {
  fullName: "Donovan Cazzarotto",
  title: "Dr. Donovan Cazzarotto",
  specialty: "Implantodontia e Ortodontia",
  crm: "17574",
  crmState: "RS",
  rqe: "",
} as const;

/**
 * ⚙️ PROFISSIONAIS que atendem/operam (cadastro do módulo Cirurgias e dos
 * filtros/badges da agenda). Fonte única.
 *
 * Clínica de UM profissional? Deixe um item — os badges de distinção não fazem
 * diferença e o filtro de profissional some sozinho. Vários? Acrescente itens;
 * a ordem é a dos selects e filtros. (Com `multiProfissional` ligado, cada um
 * ganha identidade/comissão próprias — ver features.ts.)
 *
 * Campos:
 *  - canonicalName: nome como é gravado em `cirurgias.doctor` e mostrado no select.
 *  - shortName: versão curta pra tooltip/legenda.
 *  - badgeLetter: letra do badge quando a recepção vê as agendas misturadas.
 *  - badgeClasses: classes Tailwind do badge — dê cores distintas por profissional.
 *  - matchKeywords: palavras (minúsculas, sem acento) que identificam esse
 *    profissional num texto livre (o campo `doctor` é texto livre legado).
 */
export const CLINIC_DOCTORS: ClinicDoctor[] = [
  {
    canonicalName: "Dr. Donovan Cazzarotto",
    shortName: "Dr. Donovan",
    badgeLetter: "D",
    badgeClasses: "bg-sky-100 text-sky-700 ring-sky-200",
    matchKeywords: ["donovan"],
  },
];

/**
 * E-mails com acesso às áreas restritas do titular: "Minhas finanças" e
 * "/app/dev/switch" (trocar usuário). Normalmente só o e-mail do dono do app.
 */
// Etimos primeiro: recebe o convite do bootstrap enquanto a clínica testa no staging.
export const OWNER_EMAILS: readonly string[] = ["contact@etimosia.com", "venancioaires@clinicabrasildentistas.com.br"];

/**
 * ⚙️ COBRANÇA POR PIX — só usada com a opção `pix` ligada (features.ts).
 *
 * Pix ESTÁTICO montado no próprio app (BR Code + copia-e-cola), SEM gateway: o
 * dinheiro cai direto na conta e a baixa no financeiro segue manual. Nada de
 * segredo aqui — a chave Pix é pública por natureza.
 *
 * ⚠️ Enquanto a `key` estiver vazia, o botão "Cobrar por Pix" não aparece.
 * `merchantName`/`merchantCity` vazios herdam de CLINIC_INFO; são normalizados
 * (sem acento, nome ≤25, cidade ≤15) na hora de gerar o código.
 */
export const PIX_CONFIG = {
  /** Chave Pix: CPF/CNPJ só dígitos, e-mail, telefone (+55…) ou chave aleatória. */
  key: "",
  /** Nome do recebedor no QR (≤25). Vazio = usa CLINIC_INFO.name. */
  merchantName: "",
  /** Cidade do recebedor no QR (≤15). Vazio = usa CLINIC_INFO.city. */
  merchantCity: "",
} as const;

/**
 * ⚙️ DADOS FISCAIS (NFS-e) — só usados com a opção `notaFiscal` ligada. São os
 * dados NÃO-SECRETOS do prestador; o token do Focus NFe e o ambiente ficam em
 * variáveis de ambiente (`FOCUS_NFE_TOKEN`, `FOCUS_NFE_AMBIENTE`), NUNCA aqui.
 *
 * ⚠️ Preencher com o CONTADOR do cliente — variam por município e regime:
 *  - `codigoMunicipio`: código IBGE de 7 dígitos do município do prestador.
 *  - `itemListaServico`: item da LC 116/03. Medicina "4.01"/"4.03"; odonto "4.12".
 *  - `codigoTributarioMunicipio`: código de serviço DA PREFEITURA.
 *  - `aliquota`: alíquota do ISS como FRAÇÃO (0.02 = 2%).
 *  - `issRetido`: normalmente false pra pessoa física tomadora.
 *  - `optanteSimplesNacional`: true na maioria das clínicas pequenas.
 *  - `naturezaOperacao`: 1 = tributação no município (o caso comum).
 *
 * O certificado A1 é enviado no PAINEL do Focus NFe, não passa pelo app.
 */
export const FISCAL_CONFIG = {
  /** CNPJ do prestador, só dígitos. */
  cnpj: "14856806000190",
  /** Inscrição municipal do prestador. */
  inscricaoMunicipal: "",
  /** Código IBGE (7 dígitos) do município do prestador. */
  codigoMunicipio: "4322608",
  /** Razão social do prestador (como consta no CNPJ). */
  razaoSocial: "CLINICA BRASIL IMPLANTES VENANCIO AIRES LTDA - ME",

  /** Item da lista de serviços (LC 116/03). */
  itemListaServico: "4.12",
  /** Código de serviço da prefeitura (lista municipal). */
  codigoTributarioMunicipio: "",
  /** Alíquota ISS como fração (0.02 = 2%). */
  aliquota: 0.02,
  issRetido: false,
  optanteSimplesNacional: true,
  /** Código do regime especial de tributação, se o município exigir. */
  regimeEspecialTributacao: null as number | null,
  /** 1 = tributação no município (padrão). */
  naturezaOperacao: 1,

  /** Texto padrão da discriminação do serviço, editável na hora de emitir. */
  discriminacaoPadrao: "Prestação de serviços de saúde.",
} as const;

/**
 * ⚙️ DIAS EM QUE A CLÍNICA ATENDE — índices da semana (0 = domingo … 6 = sábado).
 *
 * A grade semanal da Agenda pinta de cinza ("Dia fechado") e não deixa clicar
 * nos dias fora desta lista. O molde nasce de segunda a sexta; clínica que abre
 * SÁBADO acrescenta o 6 (`[1, 2, 3, 4, 5, 6]`). Era `isWeekend()` cravado na
 * grade — sábado ficava bloqueado mesmo pra quem atende.
 *
 * Só afeta a VISÃO semanal (onde clicar cria agendamento). Agendamento em dia
 * fora da lista continua válido se criado por outro caminho — isto é guia de
 * uso, não trava de negócio.
 */
export const DIAS_ATENDIMENTO: readonly number[] = [1, 2, 3, 4, 5, 6];

/**
 * ⚙️ FAIXA DE HORÁRIO da grade semanal da Agenda (hora cheia, 0–23).
 *
 * Define onde a grade COMEÇA e TERMINA, e o vão do almoço (que aparece
 * comprimido, como separador). Consulta marcada fora dessa faixa fica fora da
 * grade — por isso o `fim` tem que cobrir o último horário que a clínica marca.
 *
 * Era cravado 8h–18h no componente: a clínica que atende até 19h não via a
 * última hora do próprio expediente, e a que ia até 20h precisou editar o
 * código na mão. Agora é config (regra dos dois — dois clientes pediram).
 *
 * Use a faixa MAIS LARGA da semana: se só a sexta fecha mais cedo, mantenha o
 * fim do dia mais longo — dia vazio no fim da grade não atrapalha, horário que
 * não cabe some.
 */
export const HORARIO_AGENDA = {
  /** Primeira hora da grade. */
  inicio: 8,
  /** Última hora da grade (exclusiva: 18 = a grade termina às 18h00). */
  fim: 19,
  /** Início do intervalo de almoço (hora cheia). */
  almocoInicio: 12,
  /** Fim do intervalo, em minutos desde a meia-noite (13h30 = 13*60+30). */
  almocoFimMin: 13 * 60 + 30,
  /**
   * Minutos por linha da grade semanal — só a DENSIDADE do desenho.
   *
   * ⚠️ NÃO é trava de agendamento: o horário no formulário é campo livre
   * (`input type="time"`, precisão de 1 minuto) e a duração é numérica livre
   * (5–480). Consulta às 09:07 é marcada e desenhada na posição certa com
   * qualquer valor aqui — o posicionamento é contínuo, não por índice de linha.
   *
   * Diminuir dá linhas mais finas pra clicar (clínica que marca de 5 em 5, como
   * a ortodontia costuma fazer) ao custo de uma grade mais alta; aumentar
   * encurta a grade. Valores sensatos: 5, 10, 15, 20, 30 — use divisor de 60,
   * senão os rótulos de hora caem em linhas irregulares (não quebra, só fica
   * torto). O `npm run check:cliente` confere isto.
   */
  intervaloMin: 15,
} as const;

/**
 * ⚙️ CAPACIDADE DA AGENDA — quantos atendimentos cabem AO MESMO TEMPO.
 *
 * Duas travas independentes, ambas "macias": estourar uma NÃO bloqueia o
 * agendamento, só avisa e deixa a recepção "Encaixar mesmo assim" (o encaixe
 * forçado fica na trilha de auditoria). Contam só agendamentos ativos
 * (`agendado` e `atendido`); falta e cancelado liberam o horário.
 *
 * "Ao mesmo tempo" é o PICO de sobreposição dentro do horário novo, não a
 * contagem de quem apenas encosta: 14:00–14:30 e 14:30–15:00 NÃO são
 * simultâneos (o intervalo é [início, fim)).
 */
export const CAPACIDADE_AGENDA: { maxSimultaneosPorProfissional: number; cadeiras: number } = {
  /** Máximo de atendimentos simultâneos de UM mesmo profissional. 1 = sem sobreposição. */
  maxSimultaneosPorProfissional: 1,
  /**
   * Cadeiras (ou salas) da clínica = máximo de atendimentos simultâneos na
   * clínica inteira, somando todos os profissionais. Ajuste ao nº real.
   */
  cadeiras: 4,
};

/**
 * ⚙️ HOSPITAL padrão das cirurgias — só usado com o módulo `cirurgias` ligado.
 * Carimbado em cirurgia nova. Consultório que não opera em hospital pode deixar
 * vazio (""), que os campos ficam em branco pro usuário preencher.
 */
export const HOSPITAL_PADRAO = "";
/** WhatsApp do hospital, só dígitos com DDI 55. Vazio = sem envio automático. */
export const HOSPITAL_TELEFONE = "";

/**
 * ⚙️ SÓCIOS que dividem as despesas da clínica.
 *
 * O molde nasce SOLO (um "Titular", 100% dele). Com um sócio só, a UI de
 * "Divisão entre sócios" e o seletor 3/2 do lançamento de despesa SOMEM
 * sozinhos — nada pra desmontar.
 *
 * Vários sócios? Um item por pessoa (`inTwoWaySplit: true` em quem entra na
 * divisão em 2). Quem ATENDE no app liga pelo `doctorCanonicalName` — tem que
 * bater com o `canonicalName` de CLINIC_DOCTORS acima; quem não atende (ex.:
 * cônjuge que só divide despesa) fica com `null`.
 */
export const SHAREHOLDERS: Shareholder[] = [
  {
    id: "socio1",
    name: "Titular",
    shortName: "Titular",
    inTwoWaySplit: true,
    doctorCanonicalName: "Dr. Donovan Cazzarotto",
  },
];

/**
 * ⚙️ ASSISTENTE DE WHATSAPP — só usado com a capacidade `assistente` ligada
 * (features.ts). Contrato do conector em docs/ASSISTENTE-CONECTOR.md.
 *
 *  - `bookingMode`: como o assistente marca consulta pedida pelo paciente.
 *      "solicitacao" (padrão) → cria um PEDIDO em Solicitações para a recepção
 *        confirmar (exige a capacidade `solicitacoes` ligada, senão ninguém vê);
 *      "direto" → cria a consulta na agenda na hora (horário revalidado contra
 *        a grade, os bloqueios e os conflitos).
 *    Clínica nova começa em "solicitacao" até confiar no assistente.
 *    A var `ASSISTENTE_BOOKING_MODE` do Worker vale sobre este valor por
 *    ambiente (ex.: staging "direto" para testar, produção "solicitacao").
 */
export const ASSISTENTE_CONFIG: { bookingMode: "direto" | "solicitacao" } = {
  bookingMode: "solicitacao",
};
